import { Check, X } from "lucide-react";
import { evaluatePassword } from "@/lib/password-policy";

const STRENGTH_COLORS = {
  weak: "bg-red-500",
  fair: "bg-amber-500",
  strong: "bg-[var(--primary)]",
};

export function PasswordRequirements({ password }) {
  const { checks, strength, score, max } = evaluatePassword(password);
  const pct = max ? Math.round((score / max) * 100) : 0;

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1 bg-[var(--bg-elevated)] overflow-hidden">
          <div
            className={`h-full transition-all ${STRENGTH_COLORS[strength] || STRENGTH_COLORS.weak}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)] capitalize">
          {password ? strength : "—"}
        </span>
      </div>
      <ul className="space-y-1">
        {checks.map((rule) => (
          <li key={rule.id} className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
            {rule.passed ? (
              <Check size={12} className="text-[var(--primary)] shrink-0" />
            ) : (
              <X size={12} className="text-[var(--text-tertiary)] shrink-0" />
            )}
            {rule.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PasswordField({
  value,
  onChange,
  confirmValue,
  onConfirmChange,
  showConfirm = false,
  passwordPlaceholder = "Password",
  confirmPlaceholder = "Confirm password",
  confirmLabel = "",
  inputClassName = "input-dark w-full",
  id,
  confirmId,
  autoComplete = "new-password",
}) {
  const mismatch = showConfirm && confirmValue && value !== confirmValue;

  return (
    <div className="space-y-3">
      <div>
        <input
          id={id}
          className={inputClassName}
          placeholder={passwordPlaceholder}
          type="password"
          required
          minLength={8}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <PasswordRequirements password={value} />
      </div>
      {showConfirm ? (
        <div>
          {confirmLabel ? (
            <label className="block text-sm font-medium text-[#111827] mb-1.5" htmlFor={confirmId}>
              {confirmLabel}
            </label>
          ) : null}
          <input
            id={confirmId}
            className={`${inputClassName} ${mismatch ? "ring-1 ring-red-500/60" : ""}`.trim()}
            placeholder={confirmPlaceholder}
            type="password"
            required
            autoComplete="new-password"
            value={confirmValue}
            onChange={(e) => onConfirmChange(e.target.value)}
          />
          {mismatch ? (
            <p className="mt-1 text-xs text-red-500">Passwords do not match.</p>
          ) : confirmValue && value === confirmValue ? (
            <p className="mt-1 text-xs text-emerald-600">Passwords match.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
